/**
 * DAA proximity alerts (ENG-340 / ENG-345) — flat topic `daa_alerts`, same
 * broker as the fused feed. One message per ownship/intruder pair on every DAA
 * tick (~4 Hz) while the encounter lasts, plus a last one with
 * `alert_status: "resolved"`.
 *
 * The alert carries NO positions, and `bearing_deg` is relative to the
 * ownship's heading, not true north — never use it to place the intruder.
 */
import { z } from 'zod';

const num = z.number().nullable().optional();
const text = z.string().nullable().optional();

export const DaaAlertWire = z.object({
  event: z.string().optional(),
  alert_id: text,
  ownship_flight_declaration_id: z.string(),
  alert_level: z.number().int().min(0).max(3),
  alert_level_name: text,
  alert_status: z.enum(['active', 'resolved']),
  range_m: num,
  vertical_separation_m: num,
  bearing_deg: num,
  intruder_id: z.string(),
  intruder_id_type: text,
  intruder_icao: text,
  intruder_label: text,
  intruder_aircraft_id: text,
  intruder_flight_declaration_id: text,
  avoidance_advisory: text,
  timestamp: text,
});

export type DaaLevel = 0 | 1 | 2 | 3;

export interface DaaAlertEnvelope {
  /** `ownship_flight_declaration_id|intruder_id` — one line per key. */
  key: string;
  ownshipFlightId: string;
  intruderId: string;
  intruderLabel: string;
  intruderIcao: string | null;
  intruderIdType: string | null;
  /** Serial of a peer drone, when the intruder is one. */
  intruderAircraftId: string | null;
  level: DaaLevel;
  status: 'active' | 'resolved';
  rangeM: number | null;
  verticalSepM: number | null;
  bearingDeg: number | null;
  advisory: string | null;
}

export function daaPairKey(ownshipFlightId: string, intruderId: string): string {
  return `${ownshipFlightId}|${intruderId}`;
}

/** Wire message → envelope, or null when it isn't a usable DAA alert. */
export function parseDaaAlert(raw: unknown): DaaAlertEnvelope | null {
  const r = DaaAlertWire.safeParse(raw);
  if (!r.success) return null;
  const w = r.data;
  return {
    key: daaPairKey(w.ownship_flight_declaration_id, w.intruder_id),
    ownshipFlightId: w.ownship_flight_declaration_id,
    intruderId: w.intruder_id,
    intruderLabel: w.intruder_label?.trim() || `Aircraft ${w.intruder_id.toUpperCase()}`,
    intruderIcao: w.intruder_icao ?? null,
    intruderIdType: w.intruder_id_type ?? null,
    intruderAircraftId: w.intruder_aircraft_id ?? null,
    level: w.alert_level as DaaLevel,
    status: w.alert_status,
    rangeM: w.range_m ?? null,
    verticalSepM: w.vertical_separation_m ?? null,
    bearingDeg: w.bearing_deg ?? null,
    advisory: w.avoidance_advisory?.trim() || null,
  };
}
